import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { proxyModifiersForType } from '../../utils/proxyModifiers';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './proxy-sandbox-panel.css';

/**
 * ProxySandboxPanel — боковая панель фильтров/статистики для прокси.
 * Показывает дропдауны (Клиент, Сервер, Модем, Порт) и
 * секции со статистикой-чипами (Активность, ГЕО, Тариф и т.д.).
 *
 * Props:
 *   data          — массив строк ProxyTable (с полем _original)
 *   visible       — показать/скрыть панель
 *   onClose       — закрыть панель
 *   onApply       — применить фильтры ({ field: [values] })
 *   onReset       — сбросить все фильтры
 *   activeFilters — текущие активные фильтры { field: [values] }
 */

/* ---- SVG caret icon for selects ---- */
const CaretIcon = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1.41 0.59L6 5.17L10.59 0.59L12 2L6 8L0 2L1.41 0.59Z"
      fill="#FFFFFF"
    />
  </svg>
);

/* ---- SVG close icon for mobile title bar (fi-rr-cross-small 24px) ---- */
const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z"
      fill="#FFFFFF"
    />
  </svg>
);

/* ---- SVG close icon for active chips (fi-rr-cross-small 16px) ---- */
const ChipCloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41L17.59 5Z"
      fill="#FFFFFF"
    />
  </svg>
);

/* ================================================================
   Helpers
   ================================================================ */

/** Сгруппировать массив по ключу, вернуть { value, count }[] */
const groupBy = (arr, keyFn) => {
  const map = {};
  arr.forEach((item) => {
    const key = keyFn(item);
    if (key !== undefined && key !== null && key !== '') {
      map[key] = (map[key] || 0) + 1;
    }
  });
  return Object.entries(map)
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
};

/** Уникальные значения из массива */
const uniqueValues = (arr, keyFn) => {
  const set = new Set();
  arr.forEach((item) => {
    const v = keyFn(item);
    if (v !== undefined && v !== null && v !== '') set.add(v);
  });
  return Array.from(set).sort();
};

/** Нормализовать подпись ротации в значение фильтра API */
const normalizeRotationFilterValue = (value) => {
  if (value === undefined || value === null) return null;
  const raw = String(value).trim();
  if (!raw) return 'none';

  const norm = raw.toLowerCase().replace(/[\s_-]+/g, '_');
  if (norm === 'none' || norm === 'без_ротации') return 'none';
  if (norm === 'time' || norm === 'время') return 'time';
  if (norm === 'link' || norm === 'ссылка') return 'link';
  if (
    norm === 'time_link' ||
    norm === 'timelink' ||
    norm === 'время_ссылка' ||
    norm === 'время_+_ссылка'
  ) {
    return 'time_link';
  }
  return raw;
};

const normalizeOperatorChipLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return 'Неизвестно';
  const lowered = raw.toLowerCase();
  if (['unknown', 'none', 'null', 'n/a'].includes(lowered)) return 'Неизвестно';
  return raw;
};

const normalizeRotationChipLabel = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return raw;
  const norm = raw.toLowerCase().replace(/[\s_-]+/g, '_');
  if (norm === 'none' || norm === 'без_ротации') return 'По умолчанию';
  return raw;
};

const OS_FINGERPRINT_LABELS = {
  1: 'Iphone (Mac OS X)',
  2: 'Android (Linux:2.6.x)',
  3: 'Linux:2.6.x (Google crawler)',
  4: 'Linux:3.11 and newer',
  5: 'Mac OS X',
  6: 'Windows:7 or 8',
  7: 'Windows:7 (Websense crawler)',
  8: 'Windows:NT kernel 5.x',
  9: 'Windows:XP',
};

/** Служебные ключи API modem_osfp — не конкретные отпечатки, в dropdown не показываем. */
const OSFP_FILTER_SKIP_VALUES = new Set(['Выкл', 'Вкл']);

const resolveOsfpListItemLabel = (item) => {
  if (!item || typeof item !== 'object') return '';
  const displayName = String(item.display_name ?? '').trim();
  if (displayName) return displayName;
  const name = String(item.name ?? '').trim();
  if (name) return name;
  const osPart = String(item.os ?? '').trim();
  const browserPart = String(item.browser ?? '').trim();
  if (osPart && browserPart) return `${osPart} (${browserPart})`;
  return '';
};

/** Вытащить числовой ID отпечатка из значения фильтра/строки отображения. */
const extractOsfpNumericId = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  let m = raw.match(/^ID:\s*(\d+)$/i);
  if (m) return m[1];
  m = raw.match(/\(ID:\s*(\d+)\)\s*$/i);
  if (m) return m[1];
  m = raw.match(/^(\d+)$/);
  if (m) return m[1];
  return null;
};

/**
 * Подпись чипа/пункта списка для modem_osfp: имя из Kraken/таблицы, не сырой «ID: N».
 * map: modem_osfp_id -> отображаемое имя (уже заполненный useMemo).
 */
const formatModemOsfpDisplay = (value, idToNameMap) => {
  const raw = String(value ?? '').trim();
  if (!raw) return raw;
  if (raw === 'Вкл' || raw === 'Выкл') return raw;

  const id = extractOsfpNumericId(raw);
  if (id) {
    const fromMap = idToNameMap[id];
    if (fromMap) return fromMap;
    const staticLabel = OS_FINGERPRINT_LABELS[Number(id)];
    if (staticLabel) return staticLabel;
    return raw;
  }

  const stripTrailingId = raw.replace(/\s*\(ID:\s*\d+\)\s*$/i, '').trim();
  return stripTrailingId || raw;
};

/** Собрать опции dropdown «Отпечаток ОС»: только ID: N, с человекочитаемой подписью. */
const buildOsfpDropdownOptions = (rawSources, idToNameMap, statsWithCounts) => {
  const rawKeys = new Set();

  (Array.isArray(statsWithCounts) ? statsWithCounts : []).forEach((item) => {
    const filterKey = String(item?.value ?? '').trim();
    const count = Number(item?.count) || 0;
    if (!filterKey || count <= 0 || OSFP_FILTER_SKIP_VALUES.has(filterKey)) return;
    if (!extractOsfpNumericId(filterKey)) return;
    rawKeys.add(filterKey);
  });

  rawSources.forEach((source) => {
    (Array.isArray(source) ? source : []).forEach((item) => {
      if (item && typeof item === 'object') {
        const filterKey = String(item.filterValue ?? item.value ?? '').trim();
        if (filterKey) rawKeys.add(filterKey);
        return;
      }
      const filterKey = String(item ?? '').trim();
      if (filterKey) rawKeys.add(filterKey);
    });
  });

  return Array.from(rawKeys)
    .filter((filterKey) => !OSFP_FILTER_SKIP_VALUES.has(filterKey))
    .filter((filterKey) => Boolean(extractOsfpNumericId(filterKey)))
    .map((filterKey) => ({
      value: filterKey,
      label: formatModemOsfpDisplay(filterKey, idToNameMap),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'));
};

/* ================================================================
   Component
   ================================================================ */
const ProxySandboxPanel = ({
  data = [],
  visible = false,
  onClose,
  onApply,
  onReset,
  activeFilters = {},
  serverStats = null,
  operatorOptions = [],
  osFingerprintOptions = [],
  osfpList = [],
}) => {
  const panelRef = useRef(null);
  // Затемнение (на телефоне) закрывает панель только настоящим щелчком (владелец 29.10, DF85)
  const backdrop = useBackdropClose(onClose);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ---- Local filter state (synced from activeFilters on open) ----
  const [localFilters, setLocalFilters] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownSearch, setDropdownSearch] = useState({});

  useEffect(() => {
    if (visible) {
      setLocalFilters({ ...activeFilters });
      setOpenDropdown(null);
      setDropdownSearch({});
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Dropdown unique values (prefer serverStats) ----
  const clients = useMemo(() =>
    serverStats?.clients || uniqueValues(data, (r) => r.client),
  [data, serverStats]);

  const servers = useMemo(() =>
    serverStats?.servers || uniqueValues(data, (r) => r.server),
  [data, serverStats]);

  const modems = useMemo(() =>
    serverStats?.modems || uniqueValues(data, (r) => r.modem),
  [data, serverStats]);

  const ports = useMemo(() =>
    serverStats?.ports || uniqueValues(data, (r) => String(r.port)),
  [data, serverStats]);

  /** modem_osfp_id → подпись: Kraken osfp_name, справочник, разбор строк option/колонки. */
  const osfpIdToNameMap = useMemo(() => {
    const map = {};
    const soft = (id, name) => {
      if (id == null || id === '' || !name || !String(name).trim()) return;
      const k = String(id);
      if (!map[k]) map[k] = String(name).trim();
    };
    const force = (id, name) => {
      if (id == null || id === '' || !name || !String(name).trim()) return;
      map[String(id)] = String(name).trim();
    };

    Object.entries(OS_FINGERPRINT_LABELS).forEach(([id, label]) => soft(id, label));

    (osfpList || []).forEach((item) => {
      const label = resolveOsfpListItemLabel(item);
      if (label) soft(item.id, label);
    });

    (osFingerprintOptions || []).forEach((opt) => {
      const s = String(opt ?? '').trim();
      const idM = s.match(/\(ID:\s*(\d+)\)\s*$/i) || s.match(/^ID:\s*(\d+)$/i);
      const nameM = s.match(/^(.*)\s*\(ID:\s*\d+\)\s*$/i);
      if (idM && nameM && nameM[1].trim()) soft(idM[1], nameM[1].trim());
    });

    (data || []).forEach((row) => {
      const orig = row._original || {};
      const disp = String(row.osFingerprint ?? '').trim();

      if (orig.modem_osfp_id != null && orig.osfp_name != null && String(orig.osfp_name).trim() !== '') {
        force(orig.modem_osfp_id, orig.osfp_name);
      }

      const idFromDisp = extractOsfpNumericId(disp);
      const nameM = disp.match(/^(.*)\s*\(ID:\s*\d+\)\s*$/i);
      if (idFromDisp && nameM && nameM[1].trim()) {
        soft(idFromDisp, nameM[1].trim());
      }
    });

    return map;
  }, [data, osFingerprintOptions, osfpList]);

  const osFingerprints = useMemo(() => {
    const rawSources = [];
    if (Array.isArray(serverStats?.os_fingerprint) && serverStats.os_fingerprint.length > 0) {
      rawSources.push(serverStats.os_fingerprint);
    }
    if (Array.isArray(osFingerprintOptions) && osFingerprintOptions.length > 0) {
      rawSources.push(osFingerprintOptions);
    }
    rawSources.push(uniqueValues(data, (r) => {
      const orig = r._original || {};
      if (orig.modem_osfp_id != null && String(orig.modem_osfp_id).trim() !== '') {
        return `ID: ${orig.modem_osfp_id}`;
      }
      const disp = String(r.osFingerprint ?? '').trim();
      if (extractOsfpNumericId(disp)) return disp.startsWith('ID:') ? disp : `ID: ${extractOsfpNumericId(disp)}`;
      return null;
    }));

    return buildOsfpDropdownOptions(
      rawSources,
      osfpIdToNameMap,
      serverStats?.os_fingerprint_stats
    );
  }, [data, serverStats, osFingerprintOptions, osfpIdToNameMap]);

  // ---- Stats sections (prefer serverStats) ----
  const activityStats = useMemo(() => {
    if (serverStats?.activity) return serverStats.activity;
    let active = 0;
    let inactive = 0;
    data.forEach((r) => {
      const orig = r._original || {};
      if (orig.is_active === false) inactive++;
      else active++;
    });
    return [
      { value: 'Активно', count: active, color: 'green' },
      { value: 'Неактивно', count: inactive, color: 'red' },
    ];
  }, [data, serverStats]);

  const geoStats = useMemo(() =>
    serverStats?.geo || groupBy(data, (r) => r.geo),
  [data, serverStats]);

  const tariffStats = useMemo(() =>
    serverStats?.tariff || groupBy(data, (r) => r.tariff),
  [data, serverStats]);

  const availabilityStats = useMemo(() => {
    if (serverStats?.availability) return serverStats.availability;
    const map = { 'Доступен': 0, 'Недоступен': 0, 'Нет данных': 0 };
    data.forEach((r) => {
      const val = r.availability || 'Нет данных';
      if (val.includes('Доступен') && !val.includes('Не')) map['Доступен']++;
      else if (val.includes('Недоступен')) map['Недоступен']++;
      else map['Нет данных']++;
    });
    return [
      { value: 'Доступно', filterValue: 'Доступен', count: map['Доступен'], color: 'green' },
      { value: 'Недоступно', filterValue: 'Недоступен', count: map['Недоступен'], color: 'red' },
      { value: 'Нет данных', filterValue: 'Нет данных', count: map['Нет данных'], color: 'gray' },
    ];
  }, [data, serverStats]);

  const operatorStats = useMemo(() => {
    const serverOperatorStats = Array.isArray(serverStats?.operator) ? serverStats.operator : [];

    const normalizedServerStats = serverOperatorStats
      .map((item) => ({
        value: normalizeOperatorChipLabel(item?.value),
        filterValue: item?.filterValue || item?.value,
        count: Number(item?.count) || 0,
      }))
      .filter((item) => item.count > 0);

    if (normalizedServerStats.length > 0 && Array.isArray(operatorOptions) && operatorOptions.length > 0) {
      const countByLabel = normalizedServerStats.reduce((acc, item) => {
        const key = normalizeOperatorChipLabel(item.value);
        acc[key] = (acc[key] || 0) + item.count;
        return acc;
      }, {});

      return operatorOptions
        .map((operatorName) => {
          const label = normalizeOperatorChipLabel(operatorName);
          return {
            value: label,
            filterValue: operatorName,
            count: countByLabel[label] || 0,
          };
        })
        .filter((item) => item.count > 0 && item.value !== 'Неизвестно')
        .sort((a, b) => b.count - a.count);
    }

    if (normalizedServerStats.length > 0) {
      return normalizedServerStats
        .filter((item) => item.value !== 'Неизвестно')
        .sort((a, b) => b.count - a.count);
    }

    return groupBy(data, (r) => normalizeOperatorChipLabel(r.operator))
      .filter((item) => item.value !== 'Неизвестно');
  }, [data, serverStats, operatorOptions]);

  const guardStats = useMemo(() => {
    if (serverStats?.guard) return serverStats.guard;
    let on = 0;
    let off = 0;
    data.forEach((r) => {
      if (r.guard === 'Вкл') on++;
      else off++;
    });
    return [
      { value: 'Вкл', count: on, color: 'green' },
      { value: 'Выкл', count: off, color: 'red' },
    ];
  }, [data, serverStats]);

  const typeStats = useMemo(() =>
    serverStats?.type || groupBy(data, (r) => r.type),
  [data, serverStats]);

  const rotationStats = useMemo(() => {
    if (serverStats?.rotation) {
      return serverStats.rotation.map((item) => ({
        ...item,
        filterValue: item.filterValue || normalizeRotationFilterValue(item.value),
      }));
    }
    return groupBy(data, (r) => {
      const val = r.rotation || '';
      const norm = val.toLowerCase().replace(/[\s_-]+/g, '_');
      if (norm.includes('time_link') || norm.includes('timelink')) return 'Время + ссылка';
      if (norm === 'time') return 'Время';
      if (norm === 'link') return 'Ссылка';
      return val || null;
    }).map((item) => ({
      ...item,
      filterValue: normalizeRotationFilterValue(item.value),
    }));
  }, [data, serverStats]);

  // Модификаторы — только у своего протокола (DF82): Keep-Alive и gzip считают HTTP, UDP — SOCKS5
  const keepAliveStats = useMemo(() => {
    if (serverStats?.keepalive) return serverStats.keepalive;
    let on = 0;
    let off = 0;
    data.forEach((r) => {
      const orig = r._original || {};
      if (!proxyModifiersForType(orig.proxy_type).keepAlive) return;
      if (orig.is_keepalive) on++;
      else off++;
    });
    return [
      { value: 'Вкл', count: on, color: 'green' },
      { value: 'Выкл', count: off, color: 'red' },
    ];
  }, [data, serverStats]);

  const compressionStats = useMemo(() => {
    if (serverStats?.compression) return serverStats.compression;
    let on = 0;
    let off = 0;
    data.forEach((r) => {
      const orig = r._original || {};
      if (!proxyModifiersForType(orig.proxy_type).gzip) return;
      if (orig.is_compression) on++;
      else off++;
    });
    return [
      { value: 'Вкл', count: on, color: 'green' },
      { value: 'Выкл', count: off, color: 'red' },
    ];
  }, [data, serverStats]);

  const udpStats = useMemo(() => {
    if (serverStats?.udp) return serverStats.udp;
    let on = 0;
    let off = 0;
    data.forEach((r) => {
      const orig = r._original || {};
      if (!proxyModifiersForType(orig.proxy_type).udp) return;
      if (orig.is_udp_enabled) on++;
      else off++;
    });
    return [
      { value: 'Вкл', count: on, color: 'green' },
      { value: 'Выкл', count: off, color: 'red' },
    ];
  }, [data, serverStats]);

  // ---- Toggle chip in local filters ----
  const handleToggleChip = useCallback((field, value) => {
    setLocalFilters((prev) => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      const next = exists
        ? current.filter((v) => v !== value)
        : [...current, value];
      if (next.length === 0) {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      }
      return { ...prev, [field]: next };
    });
  }, []);

  // ---- Multi-select dropdown toggle ----
  const handleDropdownToggleValue = useCallback((field, value) => {
    setLocalFilters((prev) => {
      const current = prev[field] || [];
      const exists = current.includes(value);
      const next = exists
        ? current.filter((v) => v !== value)
        : [...current, value];
      if (next.length === 0) {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      }
      return { ...prev, [field]: next };
    });
  }, []);

  const handleDropdownClear = useCallback((field) => {
    setLocalFilters((prev) => {
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  }, []);

  // ---- Check if chip is active ----
  const isChipActive = useCallback((field, value) => {
    return (localFilters[field] || []).includes(value);
  }, [localFilters]);

  // ---- Apply ----
  const handleApply = useCallback(() => {
    if (onApply) onApply(localFilters);
    if (onClose) onClose();
  }, [localFilters, onApply, onClose]);

  const handleReset = useCallback(() => {
    setLocalFilters({});
    if (onReset) onReset();
    if (onClose) onClose();
  }, [onReset, onClose]);

  // ---- Close on Escape ----
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, onClose]);

  // Panel closes ONLY via the filter toggle button (onSandbox in ProxyTable)
  // or via Escape key — no outside-click dismiss.

  /* ---- Close dropdown on outside click ---- */
  const dropdownRefs = useRef({});
  useEffect(() => {
    if (!openDropdown) return;
    const handleClickOutside = (e) => {
      const ref = dropdownRefs.current[openDropdown];
      if (ref && !ref.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const searchInputRefs = useRef({});

  if (!visible) return null;

  /* ---- Render helpers ---- */

  const renderDropdown = (label, field, options) => {
    const selected = localFilters[field] || [];
    const isOpen = openDropdown === field;
    const searchValue = dropdownSearch[field] || '';
    const toDisplayValue = (val) => (
      field === 'modem_osfp' ? formatModemOsfpDisplay(val, osfpIdToNameMap) : val
    );
    const normalizedOptions = (Array.isArray(options) ? options : [])
      .map((option) => {
        if (option && typeof option === 'object') {
          const optionLabelRaw = option.label ?? option.value ?? '';
          const optionValueRaw = option.filterValue ?? option.value ?? option.label ?? '';
          const optionLabel = String(optionLabelRaw).trim();
          const optionValue = String(optionValueRaw).trim();
          if (!optionLabel || !optionValue) return null;
          return { label: optionLabel, value: optionValue };
        }
        const rawValue = String(option ?? '').trim();
        if (!rawValue) return null;
        return { label: rawValue, value: rawValue };
      })
      .filter(Boolean);
    const selectedLabelMap = normalizedOptions.reduce((acc, option) => {
      acc[option.value] = option.label;
      return acc;
    }, {});
    const displayText = selected.length === 0
      ? label
      : selected.length === 1
        ? toDisplayValue(selectedLabelMap[selected[0]] || selected[0])
        : `${label} (${selected.length})`;

    const filtered = searchValue
      ? normalizedOptions.filter((opt) => toDisplayValue(opt.label).toLowerCase().includes(searchValue.toLowerCase()))
      : normalizedOptions;

    return (
      <div
        className="psp__dropdown-card"
        ref={(el) => { dropdownRefs.current[field] = el; }}
      >
        <button
          type="button"
          className={`psp__select-trigger ${selected.length > 0 ? 'psp__select-trigger--active' : ''}`}
          onClick={() => {
            const next = isOpen ? null : field;
            setOpenDropdown(next);
            if (next) {
              setDropdownSearch((prev) => ({ ...prev, [field]: '' }));
              requestAnimationFrame(() => {
                searchInputRefs.current[field]?.focus();
              });
            }
          }}
          aria-label={label}
          aria-expanded={isOpen}
          tabIndex={0}
        >
          <span className="psp__select-text">{displayText}</span>
          {selected.length > 0 && (
            <span
              className="psp__select-clear"
              onClick={(e) => { e.stopPropagation(); handleDropdownClear(field); }}
              aria-label={`Очистить ${label}`}
              role="button"
              tabIndex={0}
            >×</span>
          )}
          <span className={`psp__select-arrow ${isOpen ? 'psp__select-arrow--open' : ''}`}><CaretIcon /></span>
        </button>

        {isOpen && (
          <div className="psp__dropdown-list">
            <div className="psp__dropdown-search-wrap">
              <input
                ref={(el) => { searchInputRefs.current[field] = el; }}
                type="text"
                className="psp__dropdown-search"
                placeholder="Поиск…"
                value={searchValue}
                onChange={(e) => setDropdownSearch((prev) => ({ ...prev, [field]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Поиск ${label}`}
              />
            </div>
            {filtered.length === 0 ? (
              <div className="psp__dropdown-empty">Ничего не найдено</div>
            ) : (
              filtered.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <label key={`${field}-${opt.value}`} className={`psp__dropdown-item ${checked ? 'psp__dropdown-item--checked' : ''}`}>
                    <input
                      type="checkbox"
                      className="psp__dropdown-checkbox"
                      checked={checked}
                      onChange={() => handleDropdownToggleValue(field, opt.value)}
                    />
                    <span className="psp__dropdown-checkmark">
                      {checked && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="#5283F4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </span>
                    <span className="psp__dropdown-label">{toDisplayValue(opt.label)}</span>
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  const renderChip = (field, value, count, color = 'gray', filterValue) => {
    const fv = filterValue || value;
    const active = isChipActive(field, fv);
    const displayValue = field === 'selected_operator'
      ? normalizeOperatorChipLabel(value)
      : field === 'modem_reconnect_type'
        ? normalizeRotationChipLabel(value)
        : field === 'modem_osfp'
          ? formatModemOsfpDisplay(value, osfpIdToNameMap)
          : value;
    return (
      <button
        key={`${field}-${value}`}
        type="button"
        className={`psp__chip ${active ? 'psp__chip--active' : ''}`}
        onClick={() => handleToggleChip(field, fv)}
        tabIndex={0}
        aria-label={`${displayValue} ${count}`}
        aria-pressed={active}
      >
        <span className="psp__chip-label">{displayValue}</span>
        <span className={`psp__chip-count psp__chip-count--${color}`}>{count}</span>
        <span className="psp__chip-close"><ChipCloseIcon /></span>
      </button>
    );
  };

  const renderSection = (title, field, items, defaultColor = 'gray') => (
    <div className="psp__section">
      <h3 className="psp__section-title">{title}</h3>
      <div className="psp__chips">
        {items.map((item) =>
          renderChip(field, item.value, item.count, item.color || defaultColor, item.filterValue)
        )}
      </div>
    </div>
  );

  const renderInlineRow = (title, field, items, defaultColor = 'gray') => (
    <div className="psp__inline-row">
      <h3 className="psp__inline-title">{title}</h3>
      <div className="psp__inline-chips">
        {items.map((item) =>
          renderChip(field, item.value, item.count, item.color || defaultColor, item.filterValue)
        )}
      </div>
    </div>
  );

    const backdropElement = isMobileViewport ? (
      <div
        className="psp-backdrop"
        {...backdrop}
        aria-hidden="true"
      />
    ) : null;

    const panelElement = (
      <div className="psp" ref={panelRef}>
          {/* Title bar — visible only on mobile via CSS */}
          <div className="psp__title-bar">
            <h2 className="psp__title-text">Фильтры</h2>
            <button
              type="button"
              className="psp__title-close"
              onClick={onClose}
              aria-label="Закрыть фильтры"
              tabIndex={0}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="psp__body">
            {/* ---- Dropdowns ---- */}
            {renderDropdown('Клиент', 'client_login', clients)}
            {renderDropdown('Сервер', 'server_name', servers)}
            {renderDropdown('Модем', 'modem', modems)}
            {renderDropdown('Порт', 'port', ports)}
            {renderDropdown('Отпечаток ОС', 'modem_osfp', osFingerprints)}

            {/* ---- Stats sections ---- */}
            {renderSection('Активность', 'is_active', activityStats)}
            {renderSection('ГЕО', 'selected_city', geoStats)}
            {renderSection('Тариф', 'tariff_plan', tariffStats)}
            {renderSection('Доступность', 'availability_status', availabilityStats)}
            {renderSection('Оператор', 'selected_operator', operatorStats)}

            {/* ---- Inline rows ---- */}
            {renderInlineRow('Страж', 'auto_replace_enabled', guardStats)}
            {renderInlineRow('Тип', 'proxy_type', typeStats)}
            {renderInlineRow('Ротация', 'modem_reconnect_type', rotationStats)}
            {renderInlineRow('Keep-Alive', 'is_keepalive', keepAliveStats)}
            {renderInlineRow('Сжатие HTTP', 'is_compression', compressionStats)}
            {renderInlineRow('UDP', 'is_udp_enabled', udpStats)}
          </div>

          {/* ---- Footer ---- */}
          <div className="psp__footer">
            <button
              type="button"
              className="psp__footer-btn psp__footer-btn--reset"
              onClick={handleReset}
              tabIndex={0}
            >
              Сбросить
            </button>
            <button
              type="button"
              className="psp__footer-btn psp__footer-btn--apply"
              onClick={handleApply}
              tabIndex={0}
            >
              Применить
            </button>
          </div>
      </div>
    );

    if (isMobileViewport && typeof document !== 'undefined') {
      return createPortal(
        <>
          {backdropElement}
          {panelElement}
        </>,
        document.body
      );
    }

    return panelElement;
};

export default ProxySandboxPanel;
